import React from "react";
import { Cpu, Globe, Code, Layers, Terminal, CheckCircle, ArrowRight } from "../components/Icons";
import { Reveal, Button, SectionHeading, PageGlow, Card } from "../components/ui";

const TECH_STACK = [
  {
    category: "Backend Engineering",
    icon: Terminal,
    color: "emerald",
    items: [
      { name: "Go Lang",    desc: "High-throughput services, concurrency-first APIs, and data pipelines at microsecond latency." },
      { name: "Java",       desc: "Enterprise-grade high-concurrency backends, JVM-tuned for millions of concurrent sessions." },
      { name: "Node.js",    desc: "Real-time API gateways, WebSocket servers, and event-driven microservices." },
      { name: "PostgreSQL", desc: "Relational data modeling with advanced indexing, partitioning, and full-text search." },
      { name: "Redis",      desc: "Sub-millisecond caching, session management, and distributed pub/sub messaging." },
    ],
  },
  {
    category: "Frontend & Mobile",
    icon: Code,
    color: "indigo",
    items: [
      { name: "Next.js",       desc: "Server-side rendering, ISR, and edge-cached web platforms built for Core Web Vitals." },
      { name: "React",         desc: "Highly composable UI systems with strict performance budgets and accessibility standards." },
      { name: "React Native",  desc: "Single-codebase cross-platform mobile apps for Android and iOS with native performance." },
      { name: "Swift / SwiftUI", desc: "First-class native macOS and iOS apps with hardware-level optimization." },
      { name: "Tailwind CSS",  desc: "Utility-first design systems that scale from prototype to production without drift." },
    ],
  },
  {
    category: "Infrastructure & DevOps",
    icon: Layers,
    color: "violet",
    items: [
      { name: "Kubernetes",    desc: "Container orchestration with autoscaling, zero-downtime rollouts, and pod-level health monitoring." },
      { name: "AWS",           desc: "ECS, RDS, CloudFront, S3, Lambda — architected for multi-region high availability." },
      { name: "Google Cloud",  desc: "GKE, BigQuery, Cloud Run for serverless compute and analytics workloads." },
      { name: "Microsoft Azure", desc: "Azure Kubernetes Service and Cosmos DB for enterprise and compliance-heavy clients." },
      { name: "Terraform",     desc: "Infrastructure as Code for reproducible, version-controlled cloud environments." },
    ],
  },
  {
    category: "AI & Automation",
    icon: Cpu,
    color: "cyan",
    items: [
      { name: "Custom AI Tooling",     desc: "In-house code generation pipelines that produce reviewed, production-ready boilerplate in minutes." },
      { name: "Schema Modeling AI",    desc: "Automated data model generation from product specifications — reducing architecture time by 70%." },
      { name: "Test Automation",       desc: "AI-assisted test generation covering unit, integration, and load test scenarios automatically." },
      { name: "LLM Integrations",      desc: "GPT-4, Claude, and Gemini integrations for client products requiring natural language capabilities." },
    ],
  },
];

const METHODOLOGY = [
  {
    phase: "Phase 1",
    title: "Pre-Code Architecture",
    desc: "Before touching a keyboard, our engineers run structured 72-hour sprint sessions using AI-assisted schema generators and architectural review checklists. We define every API contract, database schema, and system boundary upfront — eliminating scope creep and rework.",
    color: "accent",
  },
  {
    phase: "Phase 2",
    title: "Parallel Engineering Streams",
    desc: "We run frontend, backend, mobile, and infrastructure development simultaneously — not sequentially. Specialized engineers own their layers independently, integrated daily via shared API contracts. This cuts development time by 60% versus sequential waterfall approaches.",
    color: "violet",
  },
  {
    phase: "Phase 3",
    title: "AI-Augmented Development",
    desc: "Custom AI pipelines generate database migrations, CRUD layers, test fixtures, and boilerplate code in minutes. Senior engineers review and extend this output — spending their time on complex business logic, not repetitive scaffolding.",
    color: "emerald",
  },
  {
    phase: "Phase 4",
    title: "Hardened Production Deployments",
    desc: "Every deployment goes through automated load testing at 10× peak, security scanning, and multi-region staging before production. Kubernetes autoscaling is configured from Day 1, ensuring Day-15 launches handle viral traffic without intervention.",
    color: "cyan",
  },
];

const PRINCIPLES = [
  { title: "Zero Technical Debt Policy",  desc: "Every line of code is reviewed by a senior engineer. We don't ship messy code fast — we ship clean code fast." },
  { title: "100% Client IP Ownership",    desc: "All code, assets, and infrastructure belong to the client upon delivery. No vendor lock-in, ever." },
  { title: "AI-Augmented, Human-Verified", desc: "AI handles the boilerplate; our engineers handle the judgment. Speed without compromising expertise." },
  { title: "Async-First Collaboration",   desc: "Built-in documentation, Loom walkthroughs, and comprehensive handoff packages come standard with every delivery." },
];

const COLOR_MAP = {
  emerald: { border: "border-emerald-500/25", grad: "from-emerald-500/30 to-teal-500/10", text: "text-emerald-600", dot: "bg-emerald-400" },
  accent:  { border: "border-accent-500/25",  grad: "from-accent-500/30 to-blue-500/10",  text: "text-accent-600",  dot: "bg-accent-400"  },
  indigo:  { border: "border-accent-500/25",  grad: "from-accent-500/30 to-blue-500/10",  text: "text-accent-600",  dot: "bg-accent-400"  },
  violet:  { border: "border-violet-500/25",  grad: "from-violet-500/30 to-purple-500/10", text: "text-violet-600", dot: "bg-violet-400"  },
  cyan:    { border: "border-cyan-500/25",    grad: "from-cyan-500/30 to-sky-500/10",     text: "text-cyan-600",    dot: "bg-cyan-400"    },
};

export default function AboutPage({ navigate }) {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-20 px-6 text-center overflow-hidden">
        <PageGlow />
        <div className="relative max-w-4xl mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="Our Studio"
              title={<>Engineering Excellence, <span className="text-gradient-brand">At Hyper-Speed.</span></>}
              subtitle="Dowble is a premium product studio. We merge elite engineering talent with custom AI automation to ship production-grade software in a fraction of the conventional timeline — without cutting corners."
            />
          </Reveal>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-20 px-6 border-t border-zinc-200" aria-labelledby="method-heading">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-14">
            <SectionHeading
              eyebrow="The Dowble Method"
              title="How We Ship in 15 Days"
              subtitle="Our methodology isn't magic — it's the result of years of process engineering, custom tooling, and a team that's done it dozens of times."
              id="method-heading"
            />
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {METHODOLOGY.map((m, i) => {
              const c = COLOR_MAP[m.color];
              return (
                <Reveal key={m.phase} delay={i * 80}>
                  <Card interactive className={"p-7 h-full " + c.border}>
                    <p className={"text-xs font-bold uppercase tracking-wider mb-2 " + c.text}>{m.phase}</p>
                    <h3 className="text-zinc-900 font-bold text-lg mb-3 tracking-tight">{m.title}</h3>
                    <p className="text-zinc-600 text-sm leading-relaxed">{m.desc}</p>
                  </Card>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-20 px-6 border-t border-zinc-200" aria-labelledby="stack-heading">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-14">
            <SectionHeading
              eyebrow="Our Stack"
              title="Technology We Master"
              subtitle="We don't generalize across every tool — we go deep on the stack that powers high-performance, scalable systems and own it completely."
              id="stack-heading"
            />
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {TECH_STACK.map((section, i) => {
              const Icon = section.icon;
              const c = COLOR_MAP[section.color];
              return (
                <Reveal key={section.category} delay={i * 80}>
                  <Card className="p-7 h-full">
                    <div className="flex items-center gap-3 mb-6">
                      <span className={"w-11 h-11 rounded-xl bg-gradient-to-br " + c.grad + " border border-zinc-200 flex items-center justify-center " + c.text}>
                        <Icon size={20} />
                      </span>
                      <h3 className="text-zinc-900 font-bold text-base">{section.category}</h3>
                    </div>
                    <ul className="space-y-4">
                      {section.items.map((item) => (
                        <li key={item.name} className="flex gap-3">
                          <span className={"w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 " + c.dot} />
                          <div>
                            <p className="text-zinc-900 text-sm font-semibold mb-0.5">{item.name}</p>
                            <p className="text-zinc-500 text-xs leading-relaxed">{item.desc}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </Card>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-20 px-6 border-t border-zinc-200" aria-labelledby="principles-heading">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-12">
            <SectionHeading eyebrow="Our Philosophy" title="Non-Negotiable Principles" id="principles-heading" />
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.title} delay={i * 70}>
                <Card interactive className="flex gap-4 p-6 h-full">
                  <span className="text-emerald-600 flex-shrink-0 mt-0.5"><CheckCircle size={22} /></span>
                  <div>
                    <p className="text-zinc-900 font-bold text-sm mb-1">{p.title}</p>
                    <p className="text-zinc-600 text-sm leading-relaxed">{p.desc}</p>
                  </div>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 text-center border-t border-zinc-200">
        <Reveal className="max-w-2xl mx-auto">
          <span className="inline-flex w-14 h-14 rounded-2xl bg-accent-500/12 border border-accent-500/25 items-center justify-center text-accent-600 mb-6">
            <Globe size={26} />
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 mb-3">Built in Bengaluru, deployed worldwide.</h2>
          <p className="text-zinc-600 text-sm mb-8 max-w-xl mx-auto">
            From our engineering hub in Bengaluru, we've shipped products used across 40+ countries.
            We work remotely, async-first, and deliver globally.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" onClick={() => navigate("contact")}>
              Start a Project
              <ArrowRight size={16} />
            </Button>
            <Button size="lg" variant="secondary" onClick={() => navigate("careers")}>
              Join the Team
              <ArrowRight size={16} />
            </Button>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
